import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { abyssColors } from '@/theme/colors';
import { useWindowSize } from '@/hooks/useWindowSize';

type DataItem = {
  key: string;
  count: number;
};

type DisplayConfigItem = {
  fieldValue: string;
  color: string;
};

type CircularBarChartProps = {
  data: DataItem[];
  displayConfig: DisplayConfigItem[];
};

type SliceData = {
  key: string;
  count: number;
  color: string;
  startAngle: number;
  endAngle: number;
};

type HoverData = {
  label: string;
  count: number;
  x: number;
  y: number;
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (
  x: number,
  y: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number
) => {
  const startOuter = polarToCartesian(x, y, outerRadius, endAngle);
  const endOuter = polarToCartesian(x, y, outerRadius, startAngle);
  const startInner = polarToCartesian(x, y, innerRadius, endAngle);
  const endInner = polarToCartesian(x, y, innerRadius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M',
    startOuter.x,
    startOuter.y,
    'A',
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    0,
    endOuter.x,
    endOuter.y,
    'L',
    endInner.x,
    endInner.y,
    'A',
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    1,
    startInner.x,
    startInner.y,
    'L',
    startOuter.x,
    startOuter.y,
    'Z',
  ].join(' ');
};

const strokeSize = 1;

export const CircularBarChart: React.FC<CircularBarChartProps> = ({ data, displayConfig }) => {
  const [selectedLevel, setSelectedLevel] = useState<string | undefined>(undefined);
  const screenSize = useWindowSize();

  const [hoverData, setHoverData] = useState<HoverData | undefined>(undefined);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<SVGPathElement>, slice: SliceData) => {
      const { clientX, clientY } = event;
      setHoverData({
        label: slice.key === 'Calculated' ? slice.key.toLowerCase() : 'remaining',
        count: slice.count,
        x: clientX,
        y: clientY,
      });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setHoverData(undefined);
  }, []);

  const handleLevelSelection = (level: string) => {
    setSelectedLevel(selectedLevel === level ? undefined : level);
  };

  const getColorByValue = useCallback(
    (value: string) => {
      const config = displayConfig.find((configItem) => configItem.fieldValue === value);
      return config ? config.color : '#000'; // Default color if not found
    },
    [displayConfig]
  );

  const totalValue = useMemo(() => {
    return data.reduce((accumulator, item) => accumulator + item.count, 0);
  }, [data]);

  const chartData = useMemo(() => {
    let cumulativeValue = 0;
    const spaceBetweenArcs = 0.2;

    return data.map((item, index) => {
      const startAngle = (cumulativeValue / totalValue) * 360;
      cumulativeValue += item.count;
      let endAngle = (cumulativeValue / totalValue) * 360;

      if (data.length === 1 || endAngle === startAngle) {
        endAngle = startAngle + 360;
      }

      return {
        ...item,
        color: getColorByValue(item.key),
        startAngle: index === 0 ? startAngle : startAngle + spaceBetweenArcs,
        endAngle: endAngle - spaceBetweenArcs,
      };
    });
  }, [data, getColorByValue, totalValue]);

  const size = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return {
        outerRadius: 52,
        innerRadius: 43,
      };
    }
    return {
      outerRadius: 75,
      innerRadius: 62,
    };
  }, [screenSize]);

  if (data.length === 0)
    return (
      <Typography
        variant="h6"
        sx={{
          color: abyssColors.primary[400],
          whiteSpace: 'nowrap',
          mt: 1,
        }}
      >
        No data found
      </Typography>
    );

  return (
    <Stack direction="row" alignItems="center">
      <Box position="relative" sx={{ my: 1.5 }}>
        <Stack
          sx={{
            position: 'absolute',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography fontSize="1.2rem" lineHeight="normal" color={abyssColors.primary[300]}>
            Total
          </Typography>
          <Typography
            fontSize="2.4rem"
            lineHeight="normal"
            fontWeight={500}
            color={abyssColors.primary[500]}
          >
            {data[0].count}
          </Typography>
        </Stack>
        <Box sx={{ position: 'relative' }}>
          <svg
            width={(size.outerRadius + strokeSize) * 2}
            height={(size.outerRadius + strokeSize) * 2}
            viewBox={`0 0 ${size.outerRadius * 2} ${size.outerRadius * 2}`}
            fill="none"
          >
            {chartData.map((slice, index) => (
              <Tooltip
                key={`${slice.key}-${index}`}
                title={hoverData ? `${hoverData.count} ${hoverData.label} values` : ''}
                arrow
                placement="top"
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        backgroundColor: '#000000B8',
                        color: abyssColors.primary[50],
                        opacity: 1,
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        py: '10px',
                        height: '28px',
                        fontWeight: 500,
                      },
                      '& .MuiTooltip-arrow': {
                        color: '#000000B8',
                      },
                    },
                  },
                }}
              >
                <path
                  d={describeArc(
                    size.outerRadius,
                    size.outerRadius,
                    size.outerRadius - 1,
                    size.innerRadius,
                    slice.startAngle,
                    slice.endAngle
                  )}
                  fill={slice.color}
                  onClick={() => handleLevelSelection(slice.key)}
                  onMouseEnter={(event) => handleMouseEnter(event, slice)}
                  onMouseLeave={handleMouseLeave}
                  stroke="black"
                  strokeOpacity="0.2"
                  strokeWidth={strokeSize}
                  mask="url(#path-2-inside-2_25190_144739)"
                />
              </Tooltip>
            ))}
          </svg>
        </Box>
      </Box>
    </Stack>
  );
};
